import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { IconButton } from "@mui/material";
import {
  CheckCircle2,
  FileText,
  Mail,
  Menu,
  PackagePlus,
  Plus,
  Radio,
  RefreshCw,
  Save,
  Send,
  Trash2,
  Users,
} from "lucide-react";
import DoctorHeader from "../doctor_dashboard/DoctorHeader";
import DoctorSidebar, { DoctorSidebarDrawer } from "../doctor_dashboard/DoctorSidebar";

const API = process.env.REACT_APP_BACKEND_URL;

const emptyContent = {
  type: "academy_item",
  title: "",
  body: "",
  action: "",
  status: "Guide",
  audienceSex: "all",
  minAge: 0,
  maxAge: 120,
  tags: "",
  active: true,
};

const emptyPackage = {
  title: "Zaviramed First Aid Kit",
  description: "Discounted home first aid package.",
  price: 20000,
  oldPrice: 25000,
  items:
    "Digital thermometer\nBandages and plasters\nAntiseptic wipes\nEmergency care card",
  imageUrl: "",
  sortOrder: 0,
  active: true,
};

const emptyEmail = {
  audience: "all",
  emails: "",
  subject: "",
  title: "",
  body: "",
  ctaLabel: "",
  ctaUrl: "",
  minAge: "",
  maxAge: "",
  limit: 150,
};

const emptyPodcastEvent = {
  title: "",
  description: "",
  startsAt: "",
  coverImageUrl: "",
  venueType: "zoom",
  venueUrl: "",
  audience: "all",
  audienceSex: "all",
  active: true,
};

const tabs = [
  { id: "packages", label: "Wellness packages", icon: PackagePlus },
  { id: "podcasts", label: "Live podcasts", icon: Radio },
  { id: "content", label: "Dashboard content", icon: FileText },
  { id: "email", label: "Send emails", icon: Mail },
];

const audiences = [
  { value: "all", label: "All clients" },
  { value: "female", label: "Female clients" },
  { value: "male", label: "Male clients" },
  { value: "premium", label: "Premium clients" },
  { value: "free", label: "Free clients" },
  { value: "marketing_consent", label: "Clients who accepted marketing" },
  { value: "with_diagnosis", label: "Clients with diagnosis" },
  { value: "individuals", label: "Typed email addresses only" },
];

const eventAudiences = [
  { value: "all", label: "All clients" },
  { value: "paid", label: "Paid clients" },
  { value: "free", label: "Free clients" },
];

const sexAudiences = [
  { value: "all", label: "All sexes" },
  { value: "female", label: "Female clients" },
  { value: "male", label: "Male clients" },
];

const venueTypes = [
  { value: "zoom", label: "Zoom" },
  { value: "meet", label: "Google Meet" },
  { value: "x", label: "X Space" },
  { value: "tiktok", label: "TikTok" },
  { value: "youtube", label: "YouTube" },
  { value: "other", label: "Other" },
];

function authHeaders() {
  return {
    Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
  };
}

function toLines(value) {
  return Array.isArray(value) ? value.join("\n") : value || "";
}

function fromLines(value) {
  return String(value || "")
    .split("\n")
    .map((item) => item.trim())
    .filter(Boolean);
}

function formatNaira(value) {
  return `₦${Number(value || 0).toLocaleString("en-NG")}`;
}

function toDateTimeInput(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

function formatEventDate(value) {
  if (!value) return "Date not set";
  return new Date(value).toLocaleString("en-NG", {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

const inputClass =
  "h-11 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-100";
const textareaClass =
  "min-h-28 rounded-lg border border-slate-200 bg-white p-3 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-100";

const AdminClientPortalContent = () => {
  const [activeTab, setActiveTab] = useState("packages");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [content, setContent] = useState([]);
  const [packages, setPackages] = useState([]);
  const [podcastEvents, setPodcastEvents] = useState([]);
  const [contentForm, setContentForm] = useState(emptyContent);
  const [packageForm, setPackageForm] = useState(emptyPackage);
  const [podcastForm, setPodcastForm] = useState(emptyPodcastEvent);
  const [emailForm, setEmailForm] = useState(emptyEmail);
  const [emailResult, setEmailResult] = useState(null);
  const [editingContentId, setEditingContentId] = useState("");
  const [editingPackageId, setEditingPackageId] = useState("");
  const [editingPodcastId, setEditingPodcastId] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const groupedContent = useMemo(() => {
    return content.reduce((acc, item) => {
      const key = item.type || "content";
      acc[key] = acc[key] || [];
      acc[key].push(item);
      return acc;
    }, {});
  }, [content]);

  const fetchContent = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/client-portal/content`, {
        headers: authHeaders(),
      });
      setContent(res.data.content || []);
      setPackages(res.data.packages || []);
      setPodcastEvents(res.data.podcastEvents || []);
    } catch (error) {
      console.error(error);
      setMessage("Failed to load client portal content");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContent();
  }, []);

  const saveContent = async (event) => {
    event.preventDefault();
    try {
      setLoading(true);
      const payload = {
        ...contentForm,
        minAge: Number(contentForm.minAge || 0),
        maxAge: Number(contentForm.maxAge || 120),
        tags: fromLines(contentForm.tags),
        active: Boolean(contentForm.active),
      };

      if (editingContentId) {
        await axios.put(
          `${API}/admin/client-portal/content/${editingContentId}`,
          payload,
          { headers: authHeaders() }
        );
        setMessage("Content updated");
      } else {
        await axios.post(`${API}/admin/client-portal/content`, payload, {
          headers: authHeaders(),
        });
        setMessage("Content added");
      }

      setContentForm(emptyContent);
      setEditingContentId("");
      fetchContent();
    } catch (error) {
      console.error(error);
      setMessage("Could not save content");
    } finally {
      setLoading(false);
    }
  };

  const savePackage = async (event) => {
    event.preventDefault();
    try {
      setLoading(true);
      const payload = {
        ...packageForm,
        price: Number(packageForm.price || 0),
        oldPrice: Number(packageForm.oldPrice || 0),
        sortOrder: Number(packageForm.sortOrder || 0),
        items: fromLines(packageForm.items),
        active: Boolean(packageForm.active),
      };

      if (editingPackageId) {
        await axios.put(
          `${API}/admin/client-portal/packages/${editingPackageId}`,
          payload,
          { headers: authHeaders() }
        );
        setMessage("Package updated");
      } else {
        await axios.post(`${API}/admin/client-portal/packages`, payload, {
          headers: authHeaders(),
        });
        setMessage("Package added");
      }

      setPackageForm(emptyPackage);
      setEditingPackageId("");
      fetchContent();
    } catch (error) {
      console.error(error);
      setMessage("Could not save package");
    } finally {
      setLoading(false);
    }
  };

  const savePodcastEvent = async (event) => {
    event.preventDefault();
    try {
      setLoading(true);
      const payload = {
        ...podcastForm,
        active: Boolean(podcastForm.active),
      };

      if (editingPodcastId) {
        await axios.put(
          `${API}/admin/client-portal/podcast-events/${editingPodcastId}`,
          payload,
          { headers: authHeaders() }
        );
        setMessage("Podcast event updated");
      } else {
        await axios.post(`${API}/admin/client-portal/podcast-events`, payload, {
          headers: authHeaders(),
        });
        setMessage("Podcast event added");
      }

      setPodcastForm(emptyPodcastEvent);
      setEditingPodcastId("");
      fetchContent();
    } catch (error) {
      console.error(error);
      setMessage(error.response?.data?.message || "Could not save podcast event");
    } finally {
      setLoading(false);
    }
  };

  const uploadPackageImage = async (file) => {
    if (!file) return;

    try {
      setLoading(true);
      const data = new FormData();
      data.append("document", file);
      const res = await axios.post(`${API}/upload`, data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      setPackageForm((prev) => ({
        ...prev,
        imageUrl: res.data.url || "",
      }));
      setMessage("Package image uploaded");
    } catch (error) {
      console.error(error);
      setMessage("Could not upload image");
    } finally {
      setLoading(false);
    }
  };

  const uploadPodcastCover = async (file) => {
    if (!file) return;

    try {
      setLoading(true);
      const data = new FormData();
      data.append("document", file);
      const res = await axios.post(`${API}/upload`, data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      setPodcastForm((prev) => ({
        ...prev,
        coverImageUrl: res.data.url || "",
      }));
      setMessage("Podcast cover uploaded");
    } catch (error) {
      console.error(error);
      setMessage("Could not upload podcast cover");
    } finally {
      setLoading(false);
    }
  };

  const sendEmail = async (event) => {
    event.preventDefault();

    try {
      setLoading(true);
      setEmailResult(null);
      const payload = {
        ...emailForm,
        minAge: emailForm.minAge ? Number(emailForm.minAge) : undefined,
        maxAge: emailForm.maxAge ? Number(emailForm.maxAge) : undefined,
        limit: Number(emailForm.limit || 150),
      };
      const res = await axios.post(
        `${API}/admin/client-portal/email`,
        payload,
        { headers: authHeaders() }
      );
      setEmailResult(res.data);
      setMessage(res.data.message || "Email sent");
    } catch (error) {
      console.error(error);
      setMessage(error.response?.data?.message || "Could not send email");
    } finally {
      setLoading(false);
    }
  };

  const editContent = (item) => {
    setActiveTab("content");
    setEditingContentId(item._id);
    setContentForm({
      type: item.type || "academy_item",
      title: item.title || "",
      body: item.body || "",
      action: item.action || "",
      status: item.status || "Guide",
      audienceSex: item.audienceSex || "all",
      minAge: item.minAge ?? 0,
      maxAge: item.maxAge ?? 120,
      tags: toLines(item.tags),
      active: item.active !== false,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const editPackage = (item) => {
    setActiveTab("packages");
    setEditingPackageId(item._id);
    setPackageForm({
      title: item.title || "",
      description: item.description || "",
      price: item.price || 0,
      oldPrice: item.oldPrice || 0,
      items: toLines(item.items),
      imageUrl: item.imageUrl || "",
      sortOrder: item.sortOrder || 0,
      active: item.active !== false,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const editPodcastEvent = (item) => {
    setActiveTab("podcasts");
    setEditingPodcastId(item._id);
    setPodcastForm({
      title: item.title || "",
      description: item.description || "",
      startsAt: toDateTimeInput(item.startsAt),
      coverImageUrl: item.coverImageUrl || "",
      venueType: item.venueType || "other",
      venueUrl: item.venueUrl || "",
      audience: item.audience || "all",
      audienceSex: item.audienceSex || "all",
      active: item.active !== false,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const deleteContent = async (id) => {
    if (!window.confirm("Delete this content?")) return;
    await axios.delete(`${API}/admin/client-portal/content/${id}`, {
      headers: authHeaders(),
    });
    setMessage("Content deleted");
    fetchContent();
  };

  const deletePackage = async (id) => {
    if (!window.confirm("Delete this package?")) return;
    await axios.delete(`${API}/admin/client-portal/packages/${id}`, {
      headers: authHeaders(),
    });
    setMessage("Package deleted");
    fetchContent();
  };

  const deletePodcastEvent = async (id) => {
    if (!window.confirm("Delete this podcast event?")) return;
    await axios.delete(`${API}/admin/client-portal/podcast-events/${id}`, {
      headers: authHeaders(),
    });
    setMessage("Podcast event deleted");
    fetchContent();
  };

  const sendPodcastReminder = async (item) => {
    if (!window.confirm(`Send reminder emails for "${item.title}"?`)) return;

    try {
      setLoading(true);
      const res = await axios.post(
        `${API}/admin/client-portal/podcast-events/${item._id}/reminder`,
        {},
        { headers: authHeaders() }
      );
      setMessage(res.data.message || "Podcast reminders sent");
      fetchContent();
    } catch (error) {
      console.error(error);
      setMessage(error.response?.data?.message || "Could not send reminders");
    } finally {
      setLoading(false);
    }
  };

  const renderPackages = () => (
    <div className="grid gap-6 xl:grid-cols-[420px_1fr]">
      <section className="rounded-lg border border-slate-200 bg-white p-5">
        <h3 className="text-lg font-semibold text-slate-950">
          {editingPackageId ? "Update package" : "Add package"}
        </h3>
        <form className="mt-5 grid gap-4" onSubmit={savePackage}>
          <input
            className={inputClass}
            placeholder="Package title"
            value={packageForm.title}
            onChange={(e) =>
              setPackageForm({ ...packageForm, title: e.target.value })
            }
          />
          <textarea
            className={textareaClass}
            placeholder="Simple description"
            value={packageForm.description}
            onChange={(e) =>
              setPackageForm({ ...packageForm, description: e.target.value })
            }
          />
          <div className="grid gap-3 sm:grid-cols-3">
            <input
              className={inputClass}
              type="number"
              placeholder="Price"
              value={packageForm.price}
              onChange={(e) =>
                setPackageForm({ ...packageForm, price: e.target.value })
              }
            />
            <input
              className={inputClass}
              type="number"
              placeholder="Old price"
              value={packageForm.oldPrice}
              onChange={(e) =>
                setPackageForm({ ...packageForm, oldPrice: e.target.value })
              }
            />
            <input
              className={inputClass}
              type="number"
              placeholder="Sort"
              value={packageForm.sortOrder}
              onChange={(e) =>
                setPackageForm({ ...packageForm, sortOrder: e.target.value })
              }
            />
          </div>
          <textarea
            className={textareaClass}
            placeholder="One item per line"
            value={packageForm.items}
            onChange={(e) =>
              setPackageForm({ ...packageForm, items: e.target.value })
            }
          />
          <label className="grid gap-2 text-sm font-medium text-slate-600">
            Upload package image
            <input
              className={inputClass}
              type="file"
              accept="image/*"
              onChange={(e) => uploadPackageImage(e.target.files?.[0])}
            />
          </label>
          <input
            className={inputClass}
            placeholder="Image URL after upload"
            value={packageForm.imageUrl}
            onChange={(e) =>
              setPackageForm({ ...packageForm, imageUrl: e.target.value })
            }
          />
          <label className="flex items-center gap-2 text-sm font-medium text-slate-600">
            <input
              type="checkbox"
              checked={packageForm.active}
              onChange={(e) =>
                setPackageForm({ ...packageForm, active: e.target.checked })
              }
            />
            Show to clients
          </label>
          <div className="grid gap-2 sm:grid-cols-2">
            {editingPackageId && (
              <button
                type="button"
                onClick={() => {
                  setEditingPackageId("");
                  setPackageForm(emptyPackage);
                }}
                className="h-11 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600"
              >
                Cancel
              </button>
            )}
            <button
              disabled={loading}
              className="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-[#075E54] px-4 text-sm font-semibold text-white"
            >
              {editingPackageId ? <Save size={16} /> : <Plus size={16} />}
              {editingPackageId ? "Update package" : "Add package"}
            </button>
          </div>
        </form>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-5">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-lg font-semibold text-slate-950">
            Wellness packages
          </h3>
          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-500">
            {packages.length}
          </span>
        </div>
        <div className="mt-4 grid gap-3">
          {packages.map((item) => (
            <div key={item._id} className="rounded-lg border border-slate-200 p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold text-slate-950">{item.title}</p>
                  <p className="mt-1 text-sm text-slate-500">
                    {formatNaira(item.price)}
                    {item.oldPrice ? (
                      <span className="ml-2 text-slate-400 line-through">
                        {formatNaira(item.oldPrice)}
                      </span>
                    ) : null}
                  </p>
                  <p className="mt-2 line-clamp-2 text-sm text-slate-500">
                    {item.description || "No description"}
                  </p>
                </div>
                <div className="flex shrink-0 gap-2">
                  <button
                    className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold"
                    onClick={() => editPackage(item)}
                  >
                    Edit
                  </button>
                  <button
                    className="rounded-lg border border-red-100 px-3 py-2 text-xs font-semibold text-red-600"
                    onClick={() => deletePackage(item._id)}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}

          {!packages.length && (
            <p className="rounded-lg border border-dashed border-slate-200 p-6 text-center text-sm text-slate-400">
              No wellness packages yet.
            </p>
          )}
        </div>
      </section>
    </div>
  );

  const renderPodcastEvents = () => (
    <div className="grid gap-6 xl:grid-cols-[420px_1fr]">
      <section className="rounded-lg border border-slate-200 bg-white p-5">
        <h3 className="text-lg font-semibold text-slate-950">
          {editingPodcastId ? "Update live podcast" : "Add live podcast"}
        </h3>
        <form className="mt-5 grid gap-4" onSubmit={savePodcastEvent}>
          <input
            className={inputClass}
            placeholder="Podcast title"
            value={podcastForm.title}
            onChange={(e) =>
              setPodcastForm({ ...podcastForm, title: e.target.value })
            }
            required
          />
          <textarea
            className={textareaClass}
            placeholder="Simple description for clients"
            value={podcastForm.description}
            onChange={(e) =>
              setPodcastForm({ ...podcastForm, description: e.target.value })
            }
          />
          <label className="grid gap-2 text-sm font-medium text-slate-600">
            Date and time
            <input
              className={inputClass}
              type="datetime-local"
              value={podcastForm.startsAt}
              onChange={(e) =>
                setPodcastForm({ ...podcastForm, startsAt: e.target.value })
              }
              required
            />
          </label>
          <div className="grid gap-3 sm:grid-cols-2">
            <select
              className={inputClass}
              value={podcastForm.audience}
              onChange={(e) =>
                setPodcastForm({ ...podcastForm, audience: e.target.value })
              }
            >
              {eventAudiences.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
            <select
              className={inputClass}
              value={podcastForm.audienceSex}
              onChange={(e) =>
                setPodcastForm({ ...podcastForm, audienceSex: e.target.value })
              }
            >
              {sexAudiences.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <select
              className={inputClass}
              value={podcastForm.venueType}
              onChange={(e) =>
                setPodcastForm({ ...podcastForm, venueType: e.target.value })
              }
            >
              {venueTypes.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
            <input
              className={inputClass}
              placeholder="Venue link: X, Meet, Zoom, TikTok..."
              value={podcastForm.venueUrl}
              onChange={(e) =>
                setPodcastForm({ ...podcastForm, venueUrl: e.target.value })
              }
            />
          </div>
          <label className="grid gap-2 text-sm font-medium text-slate-600">
            Upload podcast cover
            <input
              className={inputClass}
              type="file"
              accept="image/*"
              onChange={(e) => uploadPodcastCover(e.target.files?.[0])}
            />
          </label>
          <input
            className={inputClass}
            placeholder="Cover URL after upload"
            value={podcastForm.coverImageUrl}
            onChange={(e) =>
              setPodcastForm({ ...podcastForm, coverImageUrl: e.target.value })
            }
          />
          <label className="flex items-center gap-2 text-sm font-medium text-slate-600">
            <input
              type="checkbox"
              checked={podcastForm.active}
              onChange={(e) =>
                setPodcastForm({ ...podcastForm, active: e.target.checked })
              }
            />
            Show to selected clients
          </label>
          <div className="grid gap-2 sm:grid-cols-2">
            {editingPodcastId && (
              <button
                type="button"
                onClick={() => {
                  setEditingPodcastId("");
                  setPodcastForm(emptyPodcastEvent);
                }}
                className="h-11 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600"
              >
                Cancel
              </button>
            )}
            <button
              disabled={loading}
              className="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-[#075E54] px-4 text-sm font-semibold text-white"
            >
              {editingPodcastId ? <Save size={16} /> : <Plus size={16} />}
              {editingPodcastId ? "Update podcast" : "Add podcast"}
            </button>
          </div>
        </form>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-5">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-lg font-semibold text-slate-950">
            Live podcast events
          </h3>
          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-500">
            {podcastEvents.length}
          </span>
        </div>
        <div className="mt-4 grid gap-3">
          {podcastEvents.map((item) => (
            <div key={item._id} className="rounded-lg border border-slate-200 p-4">
              <div className="flex items-start gap-3">
                {item.coverImageUrl ? (
                  <img
                    alt=""
                    src={
                      String(item.coverImageUrl).startsWith("http")
                        ? item.coverImageUrl
                        : `${API}${item.coverImageUrl}`
                    }
                    className="h-20 w-20 rounded-lg object-cover"
                  />
                ) : (
                  <div className="grid h-20 w-20 place-items-center rounded-lg bg-slate-100 text-slate-400">
                    <Radio size={22} />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-slate-950">{item.title}</p>
                  <p className="mt-1 text-sm text-slate-500">
                    {formatEventDate(item.startsAt)} •{" "}
                    {eventAudiences.find((aud) => aud.value === item.audience)?.label || "All clients"} •{" "}
                    {sexAudiences.find((sex) => sex.value === item.audienceSex)?.label || "All sexes"}
                  </p>
                  <p className="mt-2 line-clamp-2 text-sm text-slate-500">
                    {item.description || "No description"}
                  </p>
                  <p className="mt-2 text-xs font-semibold uppercase text-slate-400">
                    {item.venueType || "other"} {item.venueUrl ? "venue added" : "venue missing"}
                    {item.reminderSentAt
                      ? ` • last reminder ${formatEventDate(item.reminderSentAt)}`
                      : ""}
                  </p>
                </div>
                <div className="grid shrink-0 gap-2">
                  <button
                    className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold"
                    onClick={() => editPodcastEvent(item)}
                  >
                    Edit
                  </button>
                  <button
                    className="rounded-lg border border-emerald-100 px-3 py-2 text-xs font-semibold text-emerald-700"
                    onClick={() => sendPodcastReminder(item)}
                  >
                    Remind
                  </button>
                  <button
                    className="rounded-lg border border-red-100 px-3 py-2 text-xs font-semibold text-red-600"
                    onClick={() => deletePodcastEvent(item._id)}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}

          {!podcastEvents.length && (
            <p className="rounded-lg border border-dashed border-slate-200 p-6 text-center text-sm text-slate-400">
              No live podcast events yet.
            </p>
          )}
        </div>
      </section>
    </div>
  );

  const renderContent = () => (
    <div className="grid gap-6 xl:grid-cols-[420px_1fr]">
      <section className="rounded-lg border border-slate-200 bg-white p-5">
        <h3 className="text-lg font-semibold text-slate-950">
          {editingContentId ? "Update content" : "Add content"}
        </h3>
        <form className="mt-5 grid gap-4" onSubmit={saveContent}>
          <select
            className={inputClass}
            value={contentForm.type}
            onChange={(e) =>
              setContentForm({ ...contentForm, type: e.target.value })
            }
          >
            <option value="academy_item">Health Academy</option>
            <option value="care_programme">Care Programme</option>
            <option value="reminder">Reminder</option>
            <option value="quick_action">Quick Action</option>
          </select>
          <input
            className={inputClass}
            placeholder="Title"
            value={contentForm.title}
            onChange={(e) =>
              setContentForm({ ...contentForm, title: e.target.value })
            }
          />
          <textarea
            className={textareaClass}
            placeholder="Body or short explanation"
            value={contentForm.body}
            onChange={(e) =>
              setContentForm({ ...contentForm, body: e.target.value })
            }
          />
          <input
            className={inputClass}
            placeholder="Action text or link"
            value={contentForm.action}
            onChange={(e) =>
              setContentForm({ ...contentForm, action: e.target.value })
            }
          />
          <div className="grid gap-3 sm:grid-cols-4">
            <input
              className={inputClass}
              placeholder="Status"
              value={contentForm.status}
              onChange={(e) =>
                setContentForm({ ...contentForm, status: e.target.value })
              }
            />
            <select
              className={inputClass}
              value={contentForm.audienceSex}
              onChange={(e) =>
                setContentForm({ ...contentForm, audienceSex: e.target.value })
              }
            >
              <option value="all">All</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
            <input
              className={inputClass}
              type="number"
              placeholder="Min age"
              value={contentForm.minAge}
              onChange={(e) =>
                setContentForm({ ...contentForm, minAge: e.target.value })
              }
            />
            <input
              className={inputClass}
              type="number"
              placeholder="Max age"
              value={contentForm.maxAge}
              onChange={(e) =>
                setContentForm({ ...contentForm, maxAge: e.target.value })
              }
            />
          </div>
          <textarea
            className={textareaClass}
            placeholder="Tags, one per line"
            value={contentForm.tags}
            onChange={(e) =>
              setContentForm({ ...contentForm, tags: e.target.value })
            }
          />
          <label className="flex items-center gap-2 text-sm font-medium text-slate-600">
            <input
              type="checkbox"
              checked={contentForm.active}
              onChange={(e) =>
                setContentForm({ ...contentForm, active: e.target.checked })
              }
            />
            Show to clients
          </label>
          <div className="grid gap-2 sm:grid-cols-2">
            {editingContentId && (
              <button
                type="button"
                onClick={() => {
                  setEditingContentId("");
                  setContentForm(emptyContent);
                }}
                className="h-11 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600"
              >
                Cancel
              </button>
            )}
            <button
              disabled={loading}
              className="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-[#075E54] px-4 text-sm font-semibold text-white"
            >
              {editingContentId ? <Save size={16} /> : <Plus size={16} />}
              {editingContentId ? "Update content" : "Add content"}
            </button>
          </div>
        </form>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-5">
        <h3 className="text-lg font-semibold text-slate-950">
          Dashboard content
        </h3>
        <div className="mt-4 grid gap-5">
          {Object.keys(groupedContent).map((type) => (
            <div key={type}>
              <p className="mb-2 text-xs font-semibold uppercase text-slate-400">
                {type.replace("_", " ")}
              </p>
              <div className="grid gap-3">
                {groupedContent[type].map((item) => (
                  <div
                    key={item._id}
                    className="rounded-lg border border-slate-200 p-4"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-semibold text-slate-950">
                          {item.title}
                        </p>
                        <p className="mt-1 line-clamp-2 text-sm text-slate-500">
                          {item.body || item.action}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold"
                          onClick={() => editContent(item)}
                        >
                          Edit
                        </button>
                        <button
                          className="rounded-lg border border-red-100 px-3 py-2 text-xs font-semibold text-red-600"
                          onClick={() => deleteContent(item._id)}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {!content.length && (
            <p className="rounded-lg border border-dashed border-slate-200 p-6 text-center text-sm text-slate-400">
              No dashboard content yet.
            </p>
          )}
        </div>
      </section>
    </div>
  );

  const renderEmail = () => (
    <div className="grid gap-6 xl:grid-cols-[420px_1fr]">
      <section className="rounded-lg border border-slate-200 bg-white p-5">
        <h3 className="text-lg font-semibold text-slate-950">
          Send client email
        </h3>
        <form className="mt-5 grid gap-4" onSubmit={sendEmail}>
          <select
            className={inputClass}
            value={emailForm.audience}
            onChange={(e) =>
              setEmailForm({ ...emailForm, audience: e.target.value })
            }
          >
            {audiences.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
          <textarea
            className={textareaClass}
            placeholder="Extra email addresses, one per line or comma separated"
            value={emailForm.emails}
            onChange={(e) =>
              setEmailForm({ ...emailForm, emails: e.target.value })
            }
          />
          <div className="grid gap-3 sm:grid-cols-3">
            <input
              className={inputClass}
              type="number"
              placeholder="Min age"
              value={emailForm.minAge}
              onChange={(e) =>
                setEmailForm({ ...emailForm, minAge: e.target.value })
              }
            />
            <input
              className={inputClass}
              type="number"
              placeholder="Max age"
              value={emailForm.maxAge}
              onChange={(e) =>
                setEmailForm({ ...emailForm, maxAge: e.target.value })
              }
            />
            <input
              className={inputClass}
              type="number"
              placeholder="Limit"
              value={emailForm.limit}
              onChange={(e) =>
                setEmailForm({ ...emailForm, limit: e.target.value })
              }
            />
          </div>
          <input
            className={inputClass}
            placeholder="Email subject"
            value={emailForm.subject}
            onChange={(e) =>
              setEmailForm({ ...emailForm, subject: e.target.value })
            }
            required
          />
          <input
            className={inputClass}
            placeholder="Email heading"
            value={emailForm.title}
            onChange={(e) =>
              setEmailForm({ ...emailForm, title: e.target.value })
            }
          />
          <textarea
            className="min-h-48 rounded-lg border border-slate-200 bg-white p-3 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
            placeholder="Write the email message"
            value={emailForm.body}
            onChange={(e) =>
              setEmailForm({ ...emailForm, body: e.target.value })
            }
            required
          />
          <div className="grid gap-3 sm:grid-cols-2">
            <input
              className={inputClass}
              placeholder="Button text"
              value={emailForm.ctaLabel}
              onChange={(e) =>
                setEmailForm({ ...emailForm, ctaLabel: e.target.value })
              }
            />
            <input
              className={inputClass}
              placeholder="Button link"
              value={emailForm.ctaUrl}
              onChange={(e) =>
                setEmailForm({ ...emailForm, ctaUrl: e.target.value })
              }
            />
          </div>
          <button
            disabled={loading}
            className="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-[#075E54] px-4 text-sm font-semibold text-white"
          >
            <Send size={16} />
            {loading ? "Sending..." : "Send email"}
          </button>
        </form>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-5">
        <h3 className="text-lg font-semibold text-slate-950">Email summary</h3>
        <div className="mt-4 grid gap-3">
          <div className="rounded-lg border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <Users className="text-slate-500" size={18} />
              <div>
                <p className="text-sm font-semibold text-slate-950">
                  Audience
                </p>
                <p className="text-sm text-slate-500">
                  {
                    audiences.find((item) => item.value === emailForm.audience)
                      ?.label
                  }
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="text-emerald-600" size={18} />
              <div>
                <p className="text-sm font-semibold text-slate-950">
                  Send limit
                </p>
                <p className="text-sm text-slate-500">
                  Up to {Number(emailForm.limit || 150).toLocaleString()} emails
                  in this batch.
                </p>
              </div>
            </div>
          </div>

          {emailResult && (
            <div className="rounded-lg border border-emerald-100 bg-emerald-50 p-4">
              <p className="text-sm font-semibold text-emerald-800">
                {emailResult.message}
              </p>
              <p className="mt-1 text-sm text-emerald-700">
                Sent: {emailResult.sent || 0} | Failed:{" "}
                {emailResult.failed || 0}
              </p>
            </div>
          )}
        </div>
      </section>
    </div>
  );

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <DoctorHeader />

      <div className="flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3 md:hidden">
        <div>
          <p className="text-xs text-slate-400">Admin</p>
          <h1 className="text-base font-semibold text-slate-950">
            Client Content
          </h1>
        </div>
        <IconButton onClick={() => setDrawerOpen(true)}>
          <Menu />
        </IconButton>
      </div>

      <div className="flex flex-1 overflow-hidden">
        <div className="hidden lg:flex">
          <DoctorSidebar activeTab="Client Content" setActiveTab={() => {}} />
        </div>

        <DoctorSidebarDrawer
          open={drawerOpen}
          onOpen={() => setDrawerOpen(true)}
          onClose={() => setDrawerOpen(false)}
          activeTab="Client Content"
          setActiveTab={() => {}}
        />

        <main className="flex-1 overflow-y-auto">
          <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
            <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h2 className="text-2xl font-semibold tracking-[-0.03em] text-slate-950 sm:text-3xl">
                  Client Dashboard Tools
                </h2>
                <p className="mt-2 text-sm text-slate-500">
                  Manage packages, live podcasts, dashboard cards, and client emails.
                </p>
              </div>
              <button
                type="button"
                onClick={fetchContent}
                className="inline-flex h-11 items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
              >
                <RefreshCw size={16} />
                Refresh
              </button>
            </div>

            <div className="mb-5 flex flex-wrap gap-2 rounded-lg border border-slate-200 bg-white p-2">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;

                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={`inline-flex h-10 items-center gap-2 rounded-md px-3 text-sm font-semibold transition ${
                      isActive
                        ? "bg-slate-950 text-white"
                        : "text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    <Icon size={16} />
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {message && (
              <div className="mb-5 rounded-lg border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-700">
                {message}
              </div>
            )}

            {activeTab === "packages" && renderPackages()}
            {activeTab === "podcasts" && renderPodcastEvents()}
            {activeTab === "content" && renderContent()}
            {activeTab === "email" && renderEmail()}
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminClientPortalContent;
